import { Controller } from "stimulus";
import { dropSide, reorderKeys, moveKey, mergeOrder, placeLanes, markCustomOrder, createOrderSaver, disposeAll, focusLaneMenu } from "../../training_grooming_calendar/lane_order";
import { FAILED } from "../../training_grooming_calendar/block_submit";
import { toggleDisclosure, closeOverlay } from "../../training_grooming_calendar/overlay";
import { FILTER_RELOAD } from "../../training_grooming_calendar/filter_sections";

const MARKS = ["tg-drop-target", "tg-drop-before", "tg-drop-after"];

export default class extends Controller {
  static values = { url: String };

  start(event) {
    const head = event.currentTarget;
    this.drag = { key: head.dataset.laneKey, group: head.dataset.group, head };
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", head.dataset.laneKey);
    head.classList.add("tg-dragging");
  }

  over(event) {
    const head = event.currentTarget;
    if (!this.drag || head.dataset.group !== this.drag.group) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    if (head === this.drag.head || head === this.target) return;
    this.unmark();
    this.target = head;
    head.classList.add("tg-drop-target", `tg-drop-${dropSide(this.keys(this.drag.group), this.drag.key, head.dataset.laneKey)}`);
  }

  leave(event) {
    const head = event.currentTarget;
    if (head === this.target && !head.contains(event.relatedTarget)) this.unmark();
  }

  drop(event) {
    const head = event.currentTarget;
    if (!this.drag || head.dataset.group !== this.drag.group) return;
    event.preventDefault();
    const next = reorderKeys(this.keys(this.drag.group), this.drag.key, head.dataset.laneKey);
    const { group, key } = this.drag;
    this.end();
    if (!next) return;
    this.save(group, next);
    focusLaneMenu(this.element, key);
  }

  end() {
    if (this.drag) this.drag.head.classList.remove("tg-dragging");
    this.unmark();
    this.drag = null;
  }

  toggleMenu(event) {
    event.stopPropagation();
    const button = event.currentTarget;
    const list = button.nextElementSibling;
    if (toggleDisclosure({ element: list, trigger: button, root: this.element })) list.querySelector("[role='menuitem']").focus({ preventScroll: true });
  }

  moveBack(event) {
    this.move(event, -1);
  }

  moveForward(event) {
    this.move(event, 1);
  }

  move(event, direction) {
    event.stopPropagation();
    const head = event.currentTarget.closest(".tg-lane-head");
    const next = moveKey(this.keys(head.dataset.group), head.dataset.laneKey, direction);
    closeOverlay({ restoreFocus: !next });
    if (!next) return;
    this.save(head.dataset.group, next);
    focusLaneMenu(this.element, head.dataset.laneKey);
  }

  reset() {
    this.request("DELETE").finally(() => this.element.dispatchEvent(new CustomEvent("training-grooming:reload", { detail: { reason: FILTER_RELOAD } })));
  }

  forget() {
    this.savers = disposeAll(this.savers);
  }

  save(kind, keys) {
    this.savers = this.savers || {};
    this.savers[kind] = this.savers[kind] || createOrderSaver({
      confirmed: this.keys(kind),
      place: (order) => this.element.querySelectorAll(`.tg-group-lanes[data-group="${kind}"]`).forEach((container) => placeLanes(container, order)),
      send: (order) => this.request("PATCH", { kind, keys: mergeOrder(this.fullOrder(kind), order) }).then((response) => response.ok),
      notify: window.notyf,
      failed: FAILED,
      onSaved: () => markCustomOrder(this.element),
    });
    this.savers[kind].move(keys);
  }

  request(method, body = null) {
    const token = document.querySelector('meta[name="csrf-token"]');
    return fetch(this.urlValue, {
      method,
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest", "X-CSRF-Token": token ? token.content : "" },
      body: body ? JSON.stringify(body) : null,
    });
  }

  keys(group) {
    return Array.from(this.element.querySelectorAll(`.tg-lane-head[data-group="${group}"]`)).map((head) => head.dataset.laneKey);
  }

  fullOrder(kind) {
    const container = this.element.querySelector(`.tg-group-lanes[data-group="${kind}"][data-order]`);
    return container ? JSON.parse(container.dataset.order) : null;
  }

  unmark() {
    if (this.target) this.target.classList.remove(...MARKS);
    this.target = null;
  }
}
